import type {
  ConnectionEvent,
  ErrorEvent,
  ScannerStatus,
  TagEvent,
} from './types'
import {
  BRIDGE_READY_EVENT,
  initRemoteBridge,
  isRemoteBridgeReady,
  remoteConnect,
  remoteConnectRfd40Auto,
  remoteDisconnect,
  remoteGetStatus,
  remoteListRfd8500,
  subscribeRemoteConnection,
  subscribeRemoteError,
  subscribeRemoteTag,
} from './leapRemoteBridge'

export type LeapRfidNativeBridge = {
  getStatus: () => string
  listRfd8500: () => string
  connect: (type: string, name?: string | null, address?: string | null) => string
  connectRfd40Auto: () => string
  disconnect: () => string
}

declare global {
  interface Window {
    LeapRfidNative?: LeapRfidNativeBridge
    LeapRfidNativeEvents?: {
      dispatch: (name: string, detail?: unknown) => void
      on: (name: string, handler: (detail: unknown) => void) => () => void
    }
  }
}

let initStarted = false

/** Probe WebView bridge or localhost WebSocket (RFID Connector background service). */
export async function initLeapBridge(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  if (window.LeapRfidNative) return true
  return initRemoteBridge()
}

export function ensureLeapBridgeInit(): void {
  if (initStarted || typeof window === 'undefined') return
  initStarted = true
  void initLeapBridge()
}

export function isNativeBridgeAvailable(): boolean {
  if (typeof window !== 'undefined' && window.LeapRfidNative) return true
  return isRemoteBridgeReady()
}

export function getNativeBridge(): LeapRfidNativeBridge | null {
  if (!window.LeapRfidNative) return null
  return window.LeapRfidNative
}

function parseJson<T>(raw: string, fallback: T): T {
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export async function nativeGetStatusAsync(): Promise<ScannerStatus | null> {
  if (window.LeapRfidNative) {
    return parseJson<ScannerStatus>(window.LeapRfidNative.getStatus(), {
      connected: false,
      rfid_device: '',
      type: '',
    })
  }
  if (!isRemoteBridgeReady()) await initRemoteBridge()
  return remoteGetStatus()
}

export function nativeGetStatus(): ScannerStatus | null {
  const bridge = getNativeBridge()
  if (!bridge) return null
  return parseJson<ScannerStatus>(bridge.getStatus(), {
    connected: false,
    rfid_device: '',
    type: '',
  })
}

export async function nativeListRfd8500Async(): Promise<
  Array<{ name: string; address?: string; type?: string }>
> {
  if (window.LeapRfidNative) {
    const raw = parseJson<unknown>(window.LeapRfidNative.listRfd8500(), [])
    return Array.isArray(raw) ? (raw as Array<{ name: string; address?: string; type?: string }>) : []
  }
  if (!isRemoteBridgeReady()) await initRemoteBridge()
  return remoteListRfd8500()
}

export function nativeListRfd8500(): Array<{
  name: string
  address?: string
  type?: string
}> {
  const bridge = getNativeBridge()
  if (!bridge) return []
  const raw = parseJson<unknown>(bridge.listRfd8500(), [])
  return Array.isArray(raw) ? (raw as Array<{ name: string; address?: string; type?: string }>) : []
}

export async function nativeConnectAsync(
  type: string,
  name?: string,
  address?: string
): Promise<void> {
  if (window.LeapRfidNative) {
    window.LeapRfidNative.connect(type, name ?? null, address ?? null)
    return
  }
  if (!isRemoteBridgeReady()) await initRemoteBridge()
  if (!isRemoteBridgeReady()) throw new Error('RFID Connector bridge not available')
  await remoteConnect(type, name, address)
}

export function nativeConnect(type: string, name?: string, address?: string): void {
  const bridge = getNativeBridge()
  if (!bridge) throw new Error('Native RFID bridge not available')
  bridge.connect(type, name ?? null, address ?? null)
}

export async function nativeConnectRfd40AutoAsync(): Promise<void> {
  if (window.LeapRfidNative) {
    window.LeapRfidNative.connectRfd40Auto()
    return
  }
  if (!isRemoteBridgeReady()) await initRemoteBridge()
  if (!isRemoteBridgeReady()) throw new Error('RFID Connector bridge not available')
  await remoteConnectRfd40Auto()
}

export function nativeConnectRfd40Auto(): void {
  const bridge = getNativeBridge()
  if (!bridge) throw new Error('Native RFID bridge not available')
  bridge.connectRfd40Auto()
}

export async function nativeDisconnectAsync(): Promise<void> {
  if (window.LeapRfidNative) {
    window.LeapRfidNative.disconnect()
    return
  }
  if (!isRemoteBridgeReady()) return
  await remoteDisconnect()
}

export function nativeDisconnect(): void {
  const bridge = getNativeBridge()
  if (!bridge) return
  bridge.disconnect()
}

type EventName = 'onTag' | 'onConnection' | 'onError'

function listenDom(event: EventName, handler: (detail: unknown) => void): () => void {
  const prefixed = `LeapRfidNative:${event}`
  const wrap = (e: Event) => {
    const detail = (e as CustomEvent).detail
    handler(detail)
  }
  window.addEventListener(prefixed, wrap)
  window.addEventListener(event, wrap)
  return () => {
    window.removeEventListener(prefixed, wrap)
    window.removeEventListener(event, wrap)
  }
}

export function subscribeNativeTag(handler: (ev: TagEvent) => void): () => void {
  const domUnsub = listenDom('onTag', (detail) => {
    const d = (detail || {}) as TagEvent
    if (d.tagID) handler(d)
  })
  const remoteUnsub = subscribeRemoteTag((ev) => handler(ev))
  return () => {
    domUnsub()
    remoteUnsub()
  }
}

export function subscribeNativeConnection(
  handler: (ev: ConnectionEvent) => void
): () => void {
  const domUnsub = listenDom('onConnection', (detail) => {
    handler((detail || {}) as ConnectionEvent)
  })
  const remoteUnsub = subscribeRemoteConnection(handler)
  return () => {
    domUnsub()
    remoteUnsub()
  }
}

export function subscribeNativeError(handler: (ev: ErrorEvent) => void): () => void {
  const domUnsub = listenDom('onError', (detail) => {
    handler((detail || {}) as ErrorEvent)
  })
  const remoteUnsub = subscribeRemoteError(handler)
  return () => {
    domUnsub()
    remoteUnsub()
  }
}

export { BRIDGE_READY_EVENT }
